import { useState, useEffect, useContext } from "react";
import "./evaluation.scss";
import { useNavigate } from "react-router-dom";
import { Drawer, Space, Select, Dropdown, Modal } from "antd";
import axios from "axios";
import { notification } from "antd";

// context
import { AuthContext } from "../../../contexts/authContext";

// ui-component
import EmptyData from "../../../components/UI/EmptyData/EmptyData";
import Loaders from "../../../components/UI/Loaders/Loaders";

// components
import CollabInviteModal from "../../../components/CollabInviteModal/CollabInviteModal";

// icon
import {
  addIcon,
  closeIcon,
  questionMarkIcon,
  circleDotIcon,
  circleIcon,
  goBackIcon,
  userAddIcon,
  progressIcon,
  signalIcon,
  rightIcon,
  optionIcon,
  deletIcon,
  excelFileIcon,
  circleMinusIcon,
  userIcon,
} from "../../../constants/icons";

// utils
import monitorInputChange from "../../../utils/monitorInputChange";
import { exportEvaluationData } from "../../../utils/exportEvaluationData";

// data
import { maturity_options, easa_system_levels } from "./data";

// intrfaces
import { ErrorParams } from "../../../interfaces/errorParams";

interface EvaluationList {
  _id: string;
  name: string;
  evaluation_type: string;
  collaborators: {
    cid: string;
    name: string;
  }[];
  progress: string;
  actionCount: number;
}

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";

const Evaluation = () => {
  const {setUser} = useContext(AuthContext);
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const [evaluations, setEvaluations] = useState<EvaluationList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [scopeDrawer, setScopeDrawer] = useState<boolean>(false);
  const [showApproach, SetShowApproach] = useState<boolean>(false);
  const [formdata, setFormdata] = useState({
    name: "",
    description: "",
    maturity: "select",
    sub_maturity: [
      {
        question:
          "Does your concept include Explainability for operational end user?",
        answer: "Not defined",
        justification: "",
      },
      {
        question: "Does your concept employ human-AI dialogue?",
        answer: "Not defined",
        justification: "",
      },
      {
        question: "Does your concept use speech or gesture recognition?",
        answer: "Not defined",
        justification: "",
      },
    ],
    easa_system_level: "",
  });
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);
  const [collabModal, setCollabModal] = useState<boolean>(false);
  const [collabEvalId, setCollabEvalId] = useState<string>("");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteEvalId, setDeleteEvalId] = useState<string>("");
  const [collab, setCollab] = useState<{
    cid: string;
    name: string;
    eid: string;
  }>({
    eid: "",
    cid: "",
    name: "",
  });
  const [removeCollabModal, setRemoveCollabModal] = useState<boolean>(false);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const resetForm = () => {
    setFormdata({
      name: "",
      description: "",
      maturity: "select",
      sub_maturity: [
        {
          question:
            "Does your concept include Explainability for operational end user?",
          answer: "Not defined",
          justification: "",
        },
        {
          question: "Does your concept employ human-AI dialogue?",
          answer: "Not defined",
          justification: "",
        },
        {
          question: "Does your concept use speech or gesture recognition?",
          answer: "Not defined",
          justification: "",
        },
      ],
      easa_system_level: "",
    });
    SetShowApproach(false);
  };

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/evaluation/list`);
      if (res.status === 200 && res.data.success === true) {
        setEvaluations(res.data.evaluations);
      }
    } catch (error:any) {
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
     
    } finally {
      setLoading(false);
    }
  };

  const handleFormValidation = () => {
    let errorlist: {
      path: string;
      message: string;
    }[] = [];
    if (formdata.name === "" || formdata.name.length < 2) {
      errorlist.push({
        path: "name",
        message: "System name should be atleast 2 characters",
      });
    }
    if (formdata.description === "" || formdata.description.length < 3) {
      errorlist.push({
        path: "description",
        message: "System description should be atleast 3 characters",
      });
    }
    if (formdata.maturity === "select") {
      errorlist.push({
        path: "maturity",
        message: "Select proper maturity level",
      });
    }
    if (
      formdata.maturity === maturity_options[2].value ||
      formdata.maturity === maturity_options[2].value
    ) {
      if (formdata.sub_maturity[0].answer === "Not defined") {
        errorlist.push({
          path: "sub_maturity-0",
          message: "Choose your answer",
        });
      }
      if (formdata.sub_maturity[1].answer === "Not defined") {
        errorlist.push({
          path: "sub_maturity-1",
          message: "Choose your answer",
        });
      }
      if (formdata.sub_maturity[2].answer === "Not defined") {
        errorlist.push({
          path: "sub_maturity-2",
          message: "Choose your answer",
        });
      }
    }
    if (formdata.easa_system_level === "") {
      errorlist.push({
        path: "easa_system_level",
        message: "Choose your EASA system level",
      });
    }
    if (errorlist.length > 0) {
      setErrorParams(errorlist);
      return false;
    } else {
      SetShowApproach(true);
      return true;
    }
  };

  const handleCreateEvaluationScope = async (approach: string) => {
    try {
      setLoading(true);

      let data = {
        ...formdata,
        evaluation_type: approach,
      };
      const res = await axios.post(`${API_URL}/evaluation/create`, data);
      if (res.status === 200 && res.data.success === true) {
        const data = res.data.scope;
        setScopeDrawer(false);
        resetForm();
        // dynamically populate the evaluation list
        setEvaluations([
          {
            _id: data._id.toString(),
            name: data.name,
            evaluation_type: data.evaluation_type,
            collaborators: data.collaborators,
            progress: "",
            actionCount: 0,
          },
          ...evaluations,
        ]);
      }
    } catch (error:any) {
      console.log("Error creating Evaluation scope:", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvaluation = async (id: string) => {
    try {
      const res = await axios.delete(`${API_URL}/evaluation/delete/${id}`);
      if (res.status === 200) {
        if(res.data.success === true){
          setEvaluations(
            evaluations.filter((evaluation: EvaluationList) => {
              return evaluation._id !== id;
            })
          );
          setDeleteEvalId("");
          setDeleteModal(false);
        }else{
          setDeleteEvalId("");
          setDeleteModal(false);
          opNotification("error","Unauthorized Action","Not authorized to delete evaluation.")
        }
        
      }
    } catch (error:any) {
      console.log("Error deleting evaluation");
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    }
  };

  const handleRemoveCollaborator = async () => {
    try {
      const res = await axios.delete(
        `${API_URL}/evaluation/remove/collaborator/${collab.eid}/${collab.cid}`
      );
      if (res.status === 200) {
        if (res.data.success === false) {
          opNotification(
            "error",
            "Unauthorized Action",
            "Not authorized to remove collaborator."
          );
          setCollab({
            eid: "",
            cid: "",
            name: ""
          })
          setRemoveCollabModal(false);
        } else {
          setEvaluations(evaluations.map((evaluation:EvaluationList) =>{
            if(evaluation._id.toString().trim() === collab.eid.trim()){
              return {
                ...evaluation,
                collaborators: evaluation.collaborators.filter((col) =>{
                  return col.cid.trim() !== collab.cid.trim()
                })
              }
            }else{
              return evaluation;
            }
          }))

          setCollab({
            eid: "",
            cid: "",
            name: ""
          })
          setRemoveCollabModal(false);

          opNotification(
            "success",
            "Collaborator Removed",
            "Collaborator removed successfully."
          );
        }
      }
    } catch (error:any) {
      console.log("Error removing collaborator: ", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    }
  };

  const handleExportData = async (name: string, id: string) =>{
    try {
      const res = await axios.get(`${API_URL}/evaluation/export/${id}`);
      if(res.status === 200 && res.data.success === true){
        console.log(res.data)
        exportEvaluationData(name, res.data.sectionHash)
      }
    } catch (error) {
      console.log("Could not fetch export data",error)
    }
  } 

  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        let ele = document.getElementById(err.path);
        if (ele) {
          ele.style.borderColor = "red";
        }
      });
    }
  }, [errorParams]);

  useEffect(() => {
    fetchEvaluation();
    // eslint-disable-next-line
  }, []);


  return (
    <div className="evaluation">
      {contextHolder}
      <div className="content">
        <div className="appbar">
          <button
            onClick={() => {
              setScopeDrawer(true);
            }}
          >
            {addIcon} Start a new evaluation
          </button>
        </div>
        {loading ? (
          <Loaders />
        ) : (
          <>
            {evaluations.length > 0 ? (
              <div className="evaluationCardContainer">
                {evaluations.map((evaluation) => (
                  <div className="evaluationCardParent" key={evaluation._id}>
                    <div className="evaluationCard">
                      <div className="badgeContainer">
                        <span
                          className={`typeBadge ${
                            evaluation.evaluation_type === "guided-approach" &&
                            "gradient"
                          }`}
                        >
                          {evaluation.evaluation_type === "guided-approach"
                            ? "Guided approach questionnaire"
                            : "User-driven questionnaire"}
                        </span>
                      </div>
                      <div className="nameContainer">
                        <span className="evaltitle">{evaluation.name}</span>
                      </div>
                      <div className="collabContainer">
                        <span>Collaborators:</span>
                        <span
                          onClick={() => {
                            setCollabEvalId(evaluation._id.toString().trim());
                            setCollabModal(true);
                          }}
                        >
                          {userAddIcon}
                        </span>
                        {evaluation.collaborators.length > 0 && (
                          <div className="collabBadgeHolder">
                            {evaluation.collaborators.length === 1 ? (
                              <span className="badge">
                                {userIcon}
                                {evaluation.collaborators[0].name}{" "}
                                <span
                                  onClick={() => {
                                    setCollab({
                                      eid: evaluation._id,
                                      cid: evaluation.collaborators[0].cid,
                                      name: evaluation.collaborators[0].name,
                                    });
                                    setRemoveCollabModal(true);
                                  }}
                                >
                                  {closeIcon}
                                </span>
                              </span>
                            ) : (
                              <>
                                <span className="badge">
                                {userIcon}
                                {evaluation.collaborators[0].name}{" "}
                                  <span
                                    onClick={() => {
                                      setCollab({
                                        eid: evaluation._id,
                                        cid: evaluation.collaborators[0].cid,
                                        name: evaluation.collaborators[0].name,
                                      });
                                      setRemoveCollabModal(true);
                                    }}
                                  >
                                    {closeIcon}
                                  </span>
                                </span>

                                <span>1+...</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="actionContainer">
                        <span>Actions to align to requirements:</span>
                        <span
                          className="underline"
                          onClick={() => {
                            navigate(
                              `/dashboard/evaluation/questionnaire/${evaluation._id}?show-action=true`
                            );
                          }}
                        >
                          {evaluation.actionCount} actions
                        </span>
                      </div>
                      <div className="progressContainer grey">
                        <div className="iconContainer">
                          <span>
                            {progressIcon} Overall Progress of questionnaire
                          </span>
                        </div>
                        <div className="iconContainer">
                          <small>
                            {evaluation.progress} questions answered
                          </small>
                        </div>
                        <hr />
                        <div className="iconContainer">
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              navigate(
                                `/dashboard/evaluation/breakdown/${evaluation._id}`
                              );
                            }}
                          >
                            {signalIcon} See detailed breakdown
                          </span>
                        </div>
                      </div>
                      <div className="buttonHolder">
                        <button
                          className="continue"
                          onClick={() => {
                            navigate(
                              `/dashboard/evaluation/questionnaire/${evaluation._id}`
                            );
                          }}
                        >
                          {rightIcon} Continue
                        </button>
                        <Space
                          direction="vertical"
                          style={{
                            cursor: "pointer",
                          }}
                        >
                          <Dropdown
                            menu={{
                              items: [
                                {
                                  label: (
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        borderBottom: "1px solid #D9D9D9",
                                        paddingBottom: "5px",
                                      }}
                                      onClick={()=>{
                                        handleExportData(evaluation.name,evaluation._id)
                                      }}
                                    >
                                      {excelFileIcon} Export
                                    </span>
                                  ),
                                  key: "0",
                                },
                                {
                                  label: (
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        color: "#D46B08",
                                      }}
                                    >
                                      {circleMinusIcon} Reset
                                    </span>
                                  ),
                                  key: "1",
                                },
                                {
                                  label: (
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        color: "#FF4D4F",
                                      }}
                                      onClick={() => {
                                        setDeleteModal(true);
                                        setDeleteEvalId(evaluation._id);
                                      }}
                                    >
                                      {deletIcon} Delete
                                    </span>
                                  ),
                                  key: "3",
                                },
                              ],
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                            overlayStyle={{
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            <button className="option">{optionIcon}</button>
                          </Dropdown>
                        </Space>
                      </div>
                      <CollabInviteModal
                        evaluationId={collabEvalId}
                        collabModal={collabModal}
                        setCollabModal={setCollabModal}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyData />
            )}
          </>
        )}
      </div>
      <Drawer
        placement="bottom"
        closable={false}
        open={scopeDrawer}
        onClose={() => {
          setScopeDrawer(false);
        }}
        key={"bottom"}
        height="100dvh" // This ensures full height
      >
        <div className="drawerScopeContainer">
          {showApproach ? (
            <div className="drawerBar">
              <div className="closeIcon">
                <button
                  onClick={() => {
                    SetShowApproach(false);
                  }}
                >
                  {goBackIcon}
                </button>
              </div>
              <div className="titleBar">
                <span className="title">Choose the type of questionnaire</span>
              </div>
            </div>
          ) : (
            <div className="drawerBar">
              <div className="closeIcon">
                <button
                  onClick={() => {
                    setScopeDrawer(false);
                  }}
                >
                  {closeIcon}
                </button>
              </div>
              <div className="titleBar">
                <span className="title">Describe your AI System</span>
                <span className="subtitle">
                  Before you start the evaluation against the requirements,
                  please describe the main characteristics of your AI System
                </span>
              </div>
              <button
                className="actionBtnNextTop"
                onClick={handleFormValidation}
              >
                Complete Scoping
              </button>
            </div>
          )}

          {!showApproach ? (
            <div className="scopeForm">
              <div className="sectionTitle">
                <span>Generalities</span>
              </div>
              <div className="inputHolder">
                <span>
                  <span className="highlight">*</span>
                  <span>Name of your AI System :</span>
                </span>
                <input
                  type="text"
                  value={formdata.name}
                  id="name"
                  onChange={(e) => {
                    setFormdata({
                      ...formdata,
                      name: e.target.value,
                    });
                    monitorInputChange("name");
                  }}
                />
              </div>
              <div className="inputHolder">
                <span>
                  <span className="highlight">*</span>
                  <span>Short description of the system :</span>
                </span>
                <textarea
                  value={formdata.description}
                  id="description"
                  onChange={(e) => {
                    setFormdata({
                      ...formdata,
                      description: e.target.value,
                    });
                    monitorInputChange("description");
                  }}
                />
              </div>
              <div className="inputHolder">
                <span>
                  <span className="highlight">*</span>
                  <span>
                    What is the current maturity level of the technology :
                  </span>
                </span>
                <Space
                  style={{ width: "100%", height: 40 }}
                  direction="vertical"
                >
                  <Select
                    style={{
                      width: "100%",
                      height: 40,
                      whiteSpace: "nowrap",
                    }}
                    value={formdata.maturity}
                    onChange={(value: string, data: any) => {
                      setFormdata({
                        ...formdata,
                        maturity: value,
                      });
                    }}
                    options={maturity_options}
                  />
                </Space>
              </div>

              {(formdata.maturity === maturity_options[2].value ||
                formdata.maturity === maturity_options[3].value) && (
                <>
                  {formdata.sub_maturity.map((sm, index) => (
                    <div className="inputHolder" key={index}>
                      <span>
                        <span className="highlight">*</span>
                        <span>
                          {sm.question} {questionMarkIcon} :
                        </span>
                      </span>
                      <div className="radioInput">
                        <span
                          onClick={() => {
                            setFormdata({
                              ...formdata,
                              sub_maturity: formdata.sub_maturity.map(
                                (sub, subidx) => {
                                  if (subidx === index) {
                                    return {
                                      ...sub,
                                      answer: "Yes",
                                    };
                                  } else {
                                    return sub;
                                  }
                                }
                              ),
                            });
                          }}
                        >
                          {sm.answer === "Yes" ? circleDotIcon : circleIcon}
                        </span>
                        <span>Yes</span>
                      </div>
                      <div className="radioInput">
                        <span
                          onClick={() => {
                            setFormdata({
                              ...formdata,
                              sub_maturity: formdata.sub_maturity.map(
                                (sub, subidx) => {
                                  if (subidx === index) {
                                    return {
                                      ...sub,
                                      answer: "No",
                                    };
                                  } else {
                                    return sub;
                                  }
                                }
                              ),
                            });
                          }}
                        >
                          {sm.answer === "No" ? circleDotIcon : circleIcon}
                        </span>

                        <span>No</span>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div className="sectionTitle">
                <span>EASA Classification</span>
                <span className="secSubTitle">
                  Please define the level of your current system based on the
                  current{" "}
                  <a href="" target="_blank" rel="noopener noreferrer">
                    EASA regulation(2024)
                  </a>
                </span>
                <span className="secSubTitle">
                  Choose one among these levels
                </span>
              </div>
              <div className="systemLevelContainer">
                {easa_system_levels.map((sl, index) => (
                  <div
                    className={`systemLevelCard ${
                      sl.level === formdata.easa_system_level && "active"
                    }`}
                    onClick={() => {
                      setFormdata({
                        ...formdata,
                        easa_system_level: sl.level,
                      });
                    }}
                    key={index}
                  >
                    <div className="radioHolder">
                      {formdata.easa_system_level === sl.level ? (
                        <span>{circleDotIcon}</span>
                      ) : (
                        <span>{circleIcon}</span>
                      )}
                    </div>
                    <div className="cardContent">
                      <span className="level">Level {sl.level}</span>
                      <span className="title">{sl.title}</span>
                      <span className="subtitle">{sl.sub_title}</span>
                      <span className="desc">{sl.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="scopeForm">
              <div className="approachContainer">
                <div className="approachCard">
                  <div className="approachCardContent">
                    <div className="titleContainer">
                      <span className="title">Guided approach</span>
                      <span className="badge">Recommended</span>
                    </div>
                    <div className="titleContainer">
                      <span className="subtitle">QUICK, EASY</span>
                    </div>
                    <div className="description">
                      <span>
                        {`Based on the general descripion you provided
                        for \${System.name}, we have prepared a guided questionnnaire
                        that is tailored for your system.
                        `}
                      </span>
                      <span>
                        If you choose the guided approach, you will only see
                        questions and thematic areas related to the current
                        state of your system.
                      </span>
                    </div>
                    <div className="buttonHolder">
                      <button
                        className="primary"
                        onClick={() => {
                          handleCreateEvaluationScope("guided-approach");
                        }}
                      >
                        Start now with the Guided Approach (Recommended)
                      </button>
                    </div>
                  </div>
                </div>
                <div className="approachCardWhite">
                  <div className="approachCardContent">
                    <div className="titleContainer">
                      <span className="title">User-driven approach</span>
                    </div>
                    <div className="titleContainer">
                      <span className="subtitle">
                        IN-DEPTH, REQUIRES MORE TIME
                      </span>
                    </div>
                    <span className="description">
                      If you want to evaluate all(or just a selection of) the
                      individual elements of your system, regardless of its
                      current status.
                    </span>
                    <div className="buttonHolder">
                      <button
                        className="secondary"
                        onClick={() => {
                          handleCreateEvaluationScope("user-driven-approach");
                        }}
                      >
                        Start now with the User-driven Approach
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!showApproach && (
            <div className="drawerScopeFooter">
              <button
                className="actionBtnNextBottom"
                onClick={() => {
                  SetShowApproach(true);
                }}
              >
                Complete Scoping
              </button>
            </div>
          )}
        </div>
      </Drawer>
      <Modal
        open={deleteModal}
        closable={false}
        centered
        onCancel={() => {
          setDeleteModal(false);
        }}
        footer={null}
      >
        <div className="deleteEvaluationContainer">
          <span className="header">Delete Evaluation</span>
          <p>Do you want to delete this evaluation?</p>
          <div className="buttonHolder">
            <button
              onClick={() => {
                setDeleteEvalId("");
                setDeleteModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btnDelete"
              onClick={() => {
                handleDeleteEvaluation(deleteEvalId);
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={removeCollabModal}
        closable={false}
        centered
        onCancel={() => {
          setRemoveCollabModal(false);
        }}
        footer={null}
      >
        <div className="deleteEvaluationContainer">
          <span className="header">Remove Collaborator</span>
          <p>Do you want to remove {collab.name} from collaboration?</p>
          <div className="buttonHolder">
            <button
              onClick={() => {
                setCollab({ eid: "", cid: "", name: "" });
                setRemoveCollabModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="btnDelete"
              onClick={() => {
                handleRemoveCollaborator();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Evaluation;
